import type { Capability, CoverRequest, CoverStatus } from '@keetcheck/shared';
import { theme } from '../theme.js';
import { timeRange, dayLabel, time } from '../lib/shifts.js';

const QUAL_ORDER = ['TRK', 'SUP', 'SWAHS', 'RLR', 'EVR', 'C/S', '4WD', 'TRL', 'IRB', 'LS', 'TRF'];

/** "4 on · TRK 1 · SUP 2 · SWAHS 3" plus warnings in amber/red. */
export function CapabilityStrip({ cap, compact = false }: { cap: Capability; compact?: boolean }) {
  const shortCrew = cap.warnings.some((w) => w.startsWith('Crew short'));
  const codes = QUAL_ORDER.filter((c) => (cap.quals[c] ?? 0) > 0 || ['TRK', 'SUP', 'SWAHS'].includes(c));
  return (
    <div style={{ fontFamily: theme.font }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, alignItems: 'center' }}>
        <Pill tone={shortCrew ? 'danger' : 'ok'}>{cap.count} on</Pill>
        {codes.slice(0, compact ? 3 : codes.length).map((c) => (
          <Pill key={c} tone={(cap.quals[c] ?? 0) === 0 ? 'warn' : 'muted'}>{c} {cap.quals[c] ?? 0}</Pill>
        ))}
      </div>
      {cap.warnings.length > 0 && (
        <div style={{ fontSize: 12, fontWeight: 600, color: shortCrew ? theme.status.danger.fg : theme.status.warn.fg, marginTop: 4 }}>
          {cap.warnings.join(' · ')}
        </div>
      )}
    </div>
  );
}

export function Pill({ tone, children }: { tone: 'ok' | 'warn' | 'danger' | 'muted' | 'service'; children: React.ReactNode }) {
  const c = theme.status[tone];
  return (
    <span style={{
      display: 'inline-block', fontSize: 11, fontWeight: 700, padding: '2px 7px', borderRadius: 6,
      background: c.bg, color: c.fg, whiteSpace: 'nowrap',
    }}>{children}</span>
  );
}

const STATUS: Record<CoverStatus, [string, 'ok' | 'warn' | 'danger' | 'muted']> = {
  open: ['Needs cover', 'warn'], covered: ['Covered', 'ok'], expired: ['Ended uncovered', 'danger'], cancelled: ['Cancelled', 'muted'],
};

export function CoverStatusPill({ status, partly = false }: { status: CoverStatus; partly?: boolean }) {
  const [label, tone] = STATUS[status];
  return <Pill tone={tone}>{status === 'open' && partly ? 'Partly covered' : label}</Pill>;
}

/** The request's time, piece by piece: who's covering what, and what's still open. */
export function CoverTimeline({ req }: { req: CoverRequest }) {
  const segs = [
    ...req.takes.map((t) => ({ ...t, who: t.displayName, gap: false })),
    ...req.gaps.map((g) => ({ ...g, who: null as string | null, gap: true, id: `gap-${g.startsAt}` })),
  ].sort((a, b) => a.startsAt.localeCompare(b.startsAt));
  if (!segs.length) return null;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      {segs.map((s) => (
        <div key={s.id} style={{
          display: 'flex', alignItems: 'center', gap: 10, padding: '7px 10px', borderRadius: 8,
          background: s.gap ? theme.status.warn.bg : theme.status.ok.bg,
          color: s.gap ? theme.status.warn.fg : theme.status.ok.fg, fontSize: 13,
        }}>
          <span style={{ width: 150, flexShrink: 0, fontWeight: 600 }}>
            {dayLabel(s.startsAt)}<br />
            <span style={{ fontWeight: 500 }}>
              {new Date(s.endsAt).getTime() - new Date(s.startsAt).getTime() > 24 * 3600_000
                ? `${time(new Date(s.startsAt))} → ${dayLabel(s.endsAt)} ${time(new Date(s.endsAt))}`
                : timeRange(s.startsAt, s.endsAt)}
            </span>
          </span>
          <span style={{ fontWeight: 700 }}>{s.gap ? 'Nobody yet' : s.who}</span>
        </div>
      ))}
    </div>
  );
}
